'use client';

import { ErrorInfo } from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

import { Text } from '@northladder/i18n';
import {
  useDeviceImagesListQuery,
  useTenantConfigQuery,
} from '@northladder/apis';
import { ButtonV1 } from '@northladder/design-system';

import { translations } from '../../../translations';

import { useRouteURL } from '../../hooks';

interface IFallbackUIProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * -----------------------------------------------------------------------------
 * UI shown when an error occurs while occurs within the render tree of the app.
 */
export function FallbackUI({ error, errorInfo }: IFallbackUIProps) {
  const { title, subTitle, reTry, beginAfresh, restartAssessment } =
    translations.ErrorInComponentTree;

  const { sessionId, assessmentType, resetNavPath, platform } = useRouteURL();
  const { data: configData } = useTenantConfigQuery({ identifier: platform });
  const { fetchDevicesImagesList } = useDeviceImagesListQuery({
    sessionId,
    assessmentType,
    baseURL: configData?.apiBaseURL || '',
  });

  const handleRefetchImageList = () => {
    fetchDevicesImagesList();
  };

  const handleOnRestart = () => {
    resetNavPath();
  };

  return (
    <div className="flex min-h-[calc(100vh-15rem)] flex-col justify-center">
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="shadow-xs bg-white px-4 py-8 sm:rounded-lg sm:px-10">
          <div className="mx-auto px-4 text-center sm:px-6 lg:px-8">
            <div className="mx-auto flex max-w-4xl flex-col items-center py-8">
              <ExclamationCircleIcon
                aria-hidden="true"
                className="mx-auto my-12 h-16 w-16 rounded-full  bg-red-50 text-red-600"
              />
              <h1 className="text-xl font-normal tracking-tight text-red-700 sm:text-xl">
                <Text text={title} />
              </h1>
              <p className="my-8 max-w-xl font-light text-red-900">
                <Text text={subTitle} />
              </p>
              <p className="m-10 border-2 border-red-400 bg-red-50 p-8 font-light text-red-900">
                {JSON.stringify(
                  {
                    errorInPage: 'FallbackUI',
                    error,
                    errorInfo,
                    message: error?.message,
                    stack: error?.stack,
                  },
                  null,
                  2
                )}
              </p>
              <div className="flex w-full justify-end">
                <ButtonV1
                  className="mx-auto my-4 w-auto px-16"
                  isEnabled
                  isLoading={false}
                  label={reTry}
                  onClick={handleRefetchImageList}
                  type="button"
                />
              </div>

              <div className="py-4 text-sm font-light text-gray-500 dark:text-gray-400">
                <Text text={beginAfresh} />

                <button
                  className="form-link-action ms-2"
                  onClick={handleOnRestart}
                  type="button"
                >
                  <Text text={restartAssessment} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
